/**
 * Zod schemas for validating indexer responses.
 *
 * These schemas mirror the TypeScript interfaces in `./types.ts`.
 * They run only in development (see `request()`); production paths
 * keep the existing `as T` cast for zero overhead.
 *
 * When the indexer schema changes, update both the TS interface and
 * the matching Zod schema. A failing dev-mode validation surfaces
 * field-level drift at the SDK boundary instead of as opaque UI bugs.
 */
import { z } from 'zod';
const PaginationMeta = z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
});
export function paginatedSchema(item) {
    return z.object({
        data: z.array(item),
        pagination: PaginationMeta,
    });
}
export const CollectionSchema = z.object({
    address: z.string(),
    creatorAddress: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    imageUrl: z.string().nullable(),
    maxSupply: z.number(),
    maxPerWallet: z.number(),
    mintPrice: z.string(),
    mintEnabled: z.boolean(),
    mintPhase: z.number(),
    whitelistPrice: z.string(),
    revealEnabled: z.boolean(),
    revealStartTime: z.string().nullable(),
    nextItemIndex: z.number(),
    baseContent: z.string().nullable(),
    unrevealedContent: z.string().nullable(),
    startsAt: z.string(),
    mintStartTime: z.number(),
    mintEndTime: z.number(),
    royaltyNumerator: z.number(),
    royaltyDenominator: z.number(),
    royaltyDestination: z.string().nullable(),
    soulbound: z.boolean(),
    app: z.array(z.string()),
    enabled: z.boolean(),
    paymentGated: z.boolean(),
    fiatPriceCents: z.number().nullable(),
    metadataFrozen: z.boolean(),
    frozenAt: z.string().nullable(),
    freezeRequiredBeforeMint: z.boolean(),
    supplyLocked: z.boolean(),
    ownershipRenounced: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CollectionStatsSchema = z.object({
    totalMinted: z.number(),
    maxSupply: z.number(),
    uniqueOwners: z.number(),
    mintProgress: z.number(),
    revealEnabled: z.boolean(),
    mintEnabled: z.boolean(),
    mintPrice: z.string(),
    events: z.record(z.number()),
});
export const NftSchema = z.object({
    address: z.string(),
    collectionAddress: z.string(),
    index: z.number(),
    ownerAddress: z.string(),
    isRevealed: z.boolean(),
    name: z.string().nullable(),
    description: z.string().nullable(),
    imageUrl: z.string().nullable(),
    attributes: z.record(z.unknown()).nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const NftWithCollectionSchema = NftSchema.extend({
    collection: z
        .object({
        address: z.string(),
        name: z.string(),
        imageUrl: z.string().nullable(),
        revealEnabled: z.boolean(),
        revealStartTime: z.string().nullable(),
    })
        .nullable(),
});
export const IndexedEventSchema = z.object({
    id: z.string(),
    collectionAddress: z.string(),
    nftAddress: z.string().nullable(),
    eventType: z.string(),
    data: z.record(z.unknown()),
    txHash: z.string(),
    txLt: z.string(),
    timestamp: z.string(),
    createdAt: z.string(),
});
export const UserStatsSchema = z.object({
    totalCollectionsCreated: z.number(),
    totalNftsOwned: z.number(),
    totalCollectionsOwned: z.number(),
});
export const UserCollectionNftCountsSchema = z.record(z.number());
